import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  useTheme,
  alpha,
  Tooltip,
  Card,
  CardContent,
  Fade,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Divider,
  Popover,
  Backdrop,
  FormControlLabel,
  Switch
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Event as EventIcon,
  LocalDrink as LocalDrinkIcon,
  Factory as FactoryIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Inventory2 as Inventory2Icon,
  Settings as SettingsIcon,
  Science as ScienceIcon,
  LocationOn as LocationOnIcon,
  Today as TodayIcon,
  ViewDay as ViewDayIcon,
  ViewWeek as ViewWeekIcon,
  ViewModule as ViewModuleIcon,
  DateRange as DateRangeIcon,
  Group as GroupIcon,
  Scale as ScaleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  startOfDay,
  endOfDay,
  startOfYear,
  endOfYear,
  addDays, 
  subDays,
  addMonths, 
  subMonths, 
  addWeeks,
  subWeeks,
  addYears,
  subYears,
  isSameMonth, 
  isSameDay, 
  isToday,
  getHours,
  setHours,
  eachHourOfInterval
} from 'date-fns';
import { supabase } from '../../supabaseClient';

const statusColors = {
  draft: '#9e9e9e',
  planned: '#2196f3',
  scheduled: '#00bcd4',
  cancelled: '#f44336'
};

const CalendarContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  width: '100%',
  minHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
  }
}));

const BatchingCalendar = () => {
  const { companyId } = useParams();
  const { canAccessPage, canEditPage, canDeletePage } = usePermissions();
  
  const [formulations, setFormulations] = useState([]);
  const [products, setProducts] = useState([]);
  const [user, setUser] = useState(null);

  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('week');
  const [calendarBatches, setCalendarBatches] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingBatch, setViewingBatch] = useState(null);
  const [timeFormat, setTimeFormat] = useState('24hour');
  const [userProfile, setUserProfile] = useState(null);
  
  // Dialog states for product, formulation, and location selection
  const [productSelectionDialogOpen, setProductSelectionDialogOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [dialogSelectedProduct, setDialogSelectedProduct] = useState(null);
  const [formulationSelectionDialogOpen, setFormulationSelectionDialogOpen] = useState(false);
  const [formulationSearchTerm, setFormulationSearchTerm] = useState('');
  const [locationSelectionDialogOpen, setLocationSelectionDialogOpen] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  
  // Product variants state
  const [productVariants, setProductVariants] = useState([]);
  
  // Unit Configuration states
  const [unitConfigurations, setUnitConfigurations] = useState([]);
  const [selectedUnitConfig, setSelectedUnitConfig] = useState(null);
  const [unitInputs, setUnitInputs] = useState({});  // Stores per-variant inputs
  
  // Unit Configuration Creation states
  const [unitConfigDialogOpen, setUnitConfigDialogOpen] = useState(false);
  const [editingUnitConfig, setEditingUnitConfig] = useState(null);
  const [unitConfigFormData, setUnitConfigFormData] = useState({
    name: '',
    base_unit_name: 'units',
    enable_cases: true,
    case_unit_name: 'cases',
    units_per_case: 12,
    enable_pallets: true,
    pallet_unit_name: 'pallets',
    cases_per_pallet: 42
  });
  const [savingUnitConfig, setSavingUnitConfig] = useState(false);
  
  // Day batches popover state
  const [dayBatchesOpen, setDayBatchesOpen] = useState(false);
  const [dayBatchesAnchor, setDayBatchesAnchor] = useState(null);
  const [selectedDayBatches, setSelectedDayBatches] = useState([]);
  
  // Form state for new batch
  const [newBatch, setNewBatch] = useState({
    batch_number: '',
    lot_number: '',
    product_id: '',
    formulation_id: '',
    production_location: '',
    manufacture_date: null,
    expiration_date: null,
    planned_start_datetime: null,
    planned_completion_datetime: null,
    status: 'planned',
    priority: 'normal',
    production_line: '',
    shift_supervisor: '',
    planned_quantity: 100,
    planned_unit_of_measure: 'g',
    variant_distribution: [],
    unit_configuration_id: '',
    product_variant_id: '',
    product_variant: ''
  });

  useEffect(() => {
    fetchLocations();
    fetchCalendarBatches();
    fetchFormulations();
    fetchProducts();
    fetchUser();
    fetchUserProfile();
    fetchUnitConfigurations();
  }, [companyId]);

  // Navigation functions
  const navigateNext = () => {
    switch (currentView) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
      case 'year':
        setCurrentDate(addYears(currentDate, 1));
        break;
    }
  };

  const navigatePrev = () => {
    switch (currentView) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
      case 'year':
        setCurrentDate(subYears(currentDate, 1));
        break;
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      case 'week':
        const weekStart = startOfWeek(currentDate);
        const weekEnd = endOfWeek(currentDate);
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      case 'month':
        return format(currentDate, 'MMMM yyyy');
      case 'year':
        return format(currentDate, 'yyyy');
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };

  const handleDayBatchesClose = () => {
    setDayBatchesOpen(false);
    setDayBatchesAnchor(null);
    setSelectedDayBatches([]);
  };

  const handleCreateFromPopover = () => {
    handleDayBatchesClose();
    handleDateClick(selectedDate);
  };

  const fetchFormulations = async () => {
    try {
      const { data, error } = await supabase
        .from('formulations')
        .select('*')
        .eq('company_id', companyId)
        .eq('status', 'approved')
        .order('name');
      
      if (error) throw error;
      setFormulations(data || []);
    } catch (err) {
      console.error('Error fetching formulations:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('time_format_preference')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        
        if (data) {
          setUserProfile(data);
          setTimeFormat(data.time_format_preference || '12hour');
        }
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const fetchUnitConfigurations = async () => {
    try {
      const { data, error } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      
      // Sort by total units per pallet (descending), then by name
      const sortedConfigs = (data || []).sort((a, b) => {
        const getTotalUnitsPerPallet = (config) => {
          if (config.enable_pallets && config.enable_cases) {
            return config.units_per_case * config.cases_per_pallet;
          } else if (config.enable_cases) {
            return config.units_per_case;
          } else {
            return 1;
          }
        };
        
        const aTotal = getTotalUnitsPerPallet(a);
        const bTotal = getTotalUnitsPerPallet(b);
        
        if (bTotal !== aTotal) {
          return bTotal - aTotal;
        }
        return a.name.localeCompare(b.name);
      });
      
      setUnitConfigurations(sortedConfigs);
    } catch (err) {
      console.error('Error fetching unit configurations:', err);
    }
  };

  const updateTimeFormat = async (newFormat) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase
          .from('profiles')
          .update({ time_format_preference: newFormat })
          .eq('id', user.id);
        
        if (error) throw error;
        
        setTimeFormat(newFormat);
        setUserProfile(prev => ({ ...prev, time_format_preference: newFormat }));
      }
    } catch (err) {
      console.error('Error updating time format:', err);
    }
  };

  useEffect(() => {
    processCalendarBatches();
  }, [allBatches, currentDate, startDate, endDate, currentView]);

  // Permission check for calendar view
  if (!canAccessPage('production_calendar')) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 500, color: '#555' }}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ color: '#777' }}>
          You don't have permission to access the Production Calendar.
        </Typography>
      </Box>
    );
  }

  const fetchLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      
      // Sort so main locations appear first, then by name
      const sortedLocations = (data || []).sort((a, b) => {
        // Main locations first (is_main = true)
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        // Then sort by name
        return a.name.localeCompare(b.name);
      });
      
      setLocations(sortedLocations);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchCalendarBatches = async () => {
    try {
      const { data, error } = await supabase
        .from('batch_calendar')
        .select('*')
        .eq('company_id', companyId)
        .order('planned_start_date', { ascending: true });
      
      if (error) throw error;
      setAllBatches(data || []);
    } catch (err) {
      console.error('Error fetching calendar batches:', err);
      setAllBatches([]);
    }
  };

  const processCalendarBatches = () => {
    let rangeStart, rangeEnd;
    
    if (startDate && endDate) {
      // Custom date range
      rangeStart = startOfDay(startDate);
      rangeEnd = endOfDay(endDate);
    } else {
      // View-based range
      switch (currentView) {
        case 'day':
          rangeStart = startOfDay(currentDate);
          rangeEnd = endOfDay(currentDate);
          break;
        case 'week':
          rangeStart = startOfWeek(currentDate);
          rangeEnd = endOfWeek(currentDate);
          break;
        case 'month':
          rangeStart = startOfMonth(currentDate);
          rangeEnd = endOfMonth(currentDate);
          break;
        case 'year':
          rangeStart = startOfYear(currentDate);
          rangeEnd = endOfYear(currentDate);
          break;
        default:
          rangeStart = startOfMonth(currentDate);
          rangeEnd = endOfMonth(currentDate);
      }
    }
    
    const filteredBatches = allBatches.filter(batch => {
      if (!batch.planned_start_date) return false;
      const batchDate = new Date(batch.planned_start_date + 'T00:00:00');
      return batchDate >= subDays(rangeStart, 1) && batchDate <= addDays(rangeEnd, 1);
    });
    
    setCalendarBatches(filteredBatches);
  };

  const getBatchesForDate = (date) => {
    return calendarBatches.filter(batch => {
      const startDate = new Date(batch.planned_start_date + 'T00:00:00');
      // Only show on start date, not spanning multiple days
      return isSameDay(date, startDate);
    });
  };

  const getStatusColor = (status) => {
    return statusColors[status] || statusColors.draft;
  };

  const handleDateClick = (date, event) => {
    const dayBatches = calendarBatches.filter(batch => 
      batch.planned_start_date && isSameDay(new Date(batch.planned_start_date + 'T00:00:00'), date)
    );
    
    if (dayBatches.length > 0 && event) {
      setSelectedDayBatches(dayBatches);
      setSelectedDate(date);
      setDayBatchesAnchor(event.currentTarget);
      setDayBatchesOpen(true);
    } else {
      handleQuickAddOpen(date);
    }
  };

  const handleQuickAddOpen = (date) => {
    // Check edit permission before allowing new batch creation
    if (!canEditPage('production_calendar')) {
      return;
    }
    
    setSelectedDate(date);
    
    // Check if this is a specific time slot (day/week view) or just a date (month/year view)
    const isSpecificTime = currentView === 'day' || currentView === 'week';
    let startDate, endDate;
    
    if (isSpecificTime && date instanceof Date) {
      // Use the exact time from day/week view
      startDate = dayjs(date);
      endDate = dayjs(date).add(1, 'hour');
    } else {
      // Default to start of day for month/year view
      startDate = dayjs(date).startOf('day').hour(9).minute(0).second(0);
      endDate = dayjs(date).startOf('day').hour(16).minute(0).second(0);
    }
    
    // Completely reset the form to default values (not preserving previous data)
    setNewBatch({
      batch_number: '',
      lot_number: '',
      product_id: '',
      formulation_id: '',
      production_location: '',
      manufacture_date: dayjs(date).startOf('day'),
      expiration_date: null,
      planned_start_datetime: startDate,
      planned_completion_datetime: endDate,
      status: 'planned',
      priority: 'normal',
      production_line: '',
      shift_supervisor: '',
      planned_quantity: 100,
      planned_unit_of_measure: 'g',
      variant_distribution: []
    });
    
      // Reset all related state
      setEditingBatch(null);
      setIsEditing(false);
      setErrorMessage('');
      setProductVariants([]);
      setSelectedUnitConfig(null);
      setUnitInputs({});
    
    setCreateDialogOpen(true);
  };


  const handleBatchClick = async (batch, e) => {
    e.stopPropagation();
    
    // Always allow viewing, open view dialog first
    setViewingBatch(batch);
    
    // Fetch product variants if batch has a product for proper display
    if (batch.product_id) {
      await fetchProductVariants(batch.product_id);
    }
    
    setViewDialogOpen(true);
  };

  const handleEditFromView = async () => {
    // Check edit permission before allowing batch editing
    if (!canEditPage('production_calendar')) {
      return;
    }
    
    const batch = viewingBatch;
    setEditingBatch(batch);
    setNewBatch({
      ...batch,
      manufacture_date: batch.manufacture_date ? dayjs(batch.manufacture_date) : null,
      expiration_date: batch.expiration_date ? dayjs(batch.expiration_date) : null,
      planned_start_datetime: batch.planned_start_date && batch.planned_start_time ? 
        dayjs(`${batch.planned_start_date} ${batch.planned_start_time}`) : null,
      planned_completion_datetime: batch.planned_completion_date && batch.planned_completion_time ? 
        dayjs(`${batch.planned_completion_date} ${batch.planned_completion_time}`) : null,
      variant_distribution: batch.variant_distribution || [],
      product_variant_id: batch.product_variant_id || '',
      product_variant: batch.product_variant || ''
    });
    
    // Fetch product variants if batch has a product
    if (batch.product_id) {
      await fetchProductVariants(batch.product_id);
    }
    
    // Load unit configuration if batch has one
    if (batch.unit_configuration_id) {
      const unitConfig = unitConfigurations.find(config => config.id === batch.unit_configuration_id);
      setSelectedUnitConfig(unitConfig || null);
      
      // Calculate unit inputs breakdown from variant_distribution
      if (unitConfig && batch.variant_distribution && Array.isArray(batch.variant_distribution)) {
        const calculatedInputs = {};
        
        batch.variant_distribution.forEach(variantDist => {
          const totalUnits = variantDist.quantity || 0;
          
          // Calculate breakdown
          let remaining = totalUnits;
          let pallets = 0;
          let cases = 0;
          let units = 0;

          if (unitConfig.enable_pallets && unitConfig.enable_cases) {
            const unitsPerPallet = unitConfig.units_per_case * unitConfig.cases_per_pallet;
            pallets = Math.floor(remaining / unitsPerPallet);
            remaining = remaining % unitsPerPallet;
          }

          if (unitConfig.enable_cases) {
            cases = Math.floor(remaining / unitConfig.units_per_case);
            remaining = remaining % unitConfig.units_per_case;
          }

          units = remaining;

          calculatedInputs[variantDist.variant_id] = {
            pallets: pallets,
            cases: cases,
            units: units
          };
        });
        
        setUnitInputs(calculatedInputs);
      } else {
        setUnitInputs({});
      }
    } else {
      setSelectedUnitConfig(null);
      setUnitInputs({});
    }
    
    setIsEditing(true);
    setErrorMessage('');
    setViewDialogOpen(false);
    setCreateDialogOpen(true);
  };

  const handleDeleteBatch = async () => {
    if (!editingBatch) return;
    
    // Check delete permission before allowing deletion
    if (!canDeletePage('production_calendar')) {
      return;
    }
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('batch_calendar')
        .delete()
        .eq('id', editingBatch.id);
      
      if (error) throw error;
      
      setCreateDialogOpen(false);
      setEditingBatch(null);
      setIsEditing(false);
      await fetchCalendarBatches();
    } catch (error) {
      console.error('Error deleting batch:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Product dialog handlers
  const handleOpenProductSelection = () => {
    setProductSearchTerm('');
    setProductSelectionDialogOpen(true);
  };

  const handleCloseProductSelection = () => {
    setProductSelectionDialogOpen(false);
    setProductSearchTerm('');
    setDialogSelectedProduct(null);
    // Don't reset productVariants here - keep them for the variant distribution section
  };

  const handleSelectProduct = async (product) => {
    setDialogSelectedProduct(product);
    await fetchProductVariants(product.id);
    // Auto-select product in newBatch for immediate feedback
    setNewBatch(prev => ({ 
      ...prev, 
      product_id: product.id,
      product_variant_id: '', // Reset variant when product changes
      product_variant: '',
      variant_distribution: [] // Clear variant distribution
    }));
    // Reset unit inputs when product changes
    setUnitInputs({});
  };

  const fetchProductVariants = async (productId) => {
    try {
      const { data, error } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true)
        .order('volume_weight', { ascending: false });
      
      if (error) throw error;
      setProductVariants(data || []);
    } catch (err) {
      console.error('Error fetching product variants:', err);
      setProductVariants([]);
    }
  };

  const handleVariantSelection = async (variant) => {
    setNewBatch(prev => ({ 
      ...prev, 
      product_variant_id: variant.id,
      product_variant: variant.name || `${variant.volume_weight}${variant.volume_unit}`,
      variant_distribution: [] // Reset variant distribution when a specific variant is selected
    }));
    // Don't reset productVariants when closing - keep them for the variant distribution section
    setProductSelectionDialogOpen(false);
    setProductSearchTerm('');
    setDialogSelectedProduct(null);
  };

  const handleConfirmProductSelection = async () => {
    if (!dialogSelectedProduct) return;
    
    // If there are no variants, we can proceed with just the product
    if (!productVariants || productVariants.length === 0) {
      setNewBatch(prev => ({ 
        ...prev, 
        product_id: dialogSelectedProduct.id,
        product_variant_id: '',
        product_variant: '',
        variant_distribution: []
      }));
      handleCloseProductSelection();
    }
    // If there are variants but none selected, don't close the dialog
    // The user must select a variant first
  };

  const getVariantQuantity = (variantId) => {
    const distribution = newBatch.variant_distribution || [];
    const variant = distribution.find(d => d.variant_id === variantId);
    return variant ? variant.quantity : 0;
  };

  const getVariantInputValue = (variantId) => {
    return getVariantQuantity(variantId) || '';
  };

  const handleVariantInputChange = (variantId, inputValue) => {
    const quantity = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
    handleVariantDistributionChange(variantId, inputValue);
  };

  const handleVariantDistributionChange = (variantId, inputValue) => {
    const quantity = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
    
    const currentDistribution = newBatch.variant_distribution || [];
    let newDistribution;
    
    const existingIndex = currentDistribution.findIndex(d => d.variant_id === variantId);
    
    if (existingIndex >= 0) {
      if (quantity > 0) {
        newDistribution = [...currentDistribution];
        newDistribution[existingIndex] = { variant_id: variantId, quantity };
      } else {
        newDistribution = currentDistribution.filter(d => d.variant_id !== variantId);
      }
    } else if (quantity > 0) {
      newDistribution = [...currentDistribution, { variant_id: variantId, quantity }];
    } else {
      newDistribution = currentDistribution;
    }
    
    setNewBatch(prev => ({
      ...prev,
      variant_distribution: newDistribution,
    }));
  };

  const calculateTotalProduction = () => {
    const distribution = newBatch.variant_distribution || [];
    if (distribution.length === 0) {
      return { totalUnits: 0, totalVolume: 0, productionUnit: 'g' };
    }
    
    const totalUnits = distribution.reduce((sum, d) => sum + d.quantity, 0);
    const totalVolume = distribution.reduce((sum, d) => {
      const variant = productVariants.find(v => v.id === d.variant_id);
      return variant ? sum + (d.quantity * variant.volume_weight) : sum;
    }, 0);
    
    const firstVariant = productVariants[0];
    const productionUnit = firstVariant ? firstVariant.volume_unit : 'g';
    
    return { totalUnits, totalVolume, productionUnit };
  };

  const calculateTotalUnitsFromInputs = (variantId, inputsOverride = null) => {
    if (!selectedUnitConfig || !variantId) return 0;
    
    const variantInputs = inputsOverride || unitInputs[variantId] || { pallets: 0, cases: 0, units: 0 };
    let total = 0;
    
    // Add units from pallets
    if (selectedUnitConfig.enable_pallets && selectedUnitConfig.enable_cases) {
      total += (variantInputs.pallets || 0) * selectedUnitConfig.cases_per_pallet * selectedUnitConfig.units_per_case;
    }
    
    // Add units from cases
    if (selectedUnitConfig.enable_cases) {
      total += (variantInputs.cases || 0) * selectedUnitConfig.units_per_case;
    }
    
    // Add individual units
    total += (variantInputs.units || 0);
    
    return total;
  };

  const updateVariantFromUnitInputs = (variantId, newInputs) => {
    if (!selectedUnitConfig) return;
    
    const totalUnits = calculateTotalUnitsFromInputs(variantId, newInputs);
    
    // Update only this specific variant in the distribution
    const currentDistribution = newBatch.variant_distribution || [];
    let newDistribution = [...currentDistribution];
    
    // Find existing entry for this variant
    const existingIndex = newDistribution.findIndex(d => d.variant_id === variantId);
    
    if (totalUnits > 0) {
      if (existingIndex >= 0) {
        // Update existing entry
        newDistribution[existingIndex] = { variant_id: variantId, quantity: totalUnits };
      } else {
        // Add new entry
        newDistribution.push({ variant_id: variantId, quantity: totalUnits });
      }
    } else {
      // Remove entry if quantity is 0
      if (existingIndex >= 0) {
        newDistribution.splice(existingIndex, 1);
      }
    }
    
    const totalProduction = calculateTotalProductionFromDistribution(newDistribution);
    
    setNewBatch(prev => ({
      ...prev,
      variant_distribution: newDistribution,
      planned_quantity: totalProduction.totalVolume,
      planned_unit_of_measure: totalProduction.productionUnit
    }));
  };

  const calculateTotalProductionFromDistribution = (distribution) => {
    let totalUnits = 0;
    let totalVolume = 0;
    
    distribution.forEach(entry => {
      const variant = productVariants.find(v => v.id === entry.variant_id);
      if (variant) {
        totalUnits += entry.quantity;
        totalVolume += entry.quantity * variant.volume_weight;
      }
    });
    
    const productionUnit = productVariants[0]?.volume_unit || 'g';
    
    return { 
      totalUnits, 
      totalVolume,
      productionUnit
    };
  };

  // Unit Configuration Management Functions
  const getConfigDescription = (config) => {
    if (!config.enable_cases && !config.enable_pallets) {
      return `${config.base_unit_name} only`;
    } else if (config.enable_cases && !config.enable_pallets) {
      return `${config.base_unit_name} → ${config.case_unit_name}`;
    } else {
      return `${config.base_unit_name} → ${config.case_unit_name} → ${config.pallet_unit_name}`;
    }
  };

  const handleUnitConfigSubmit = async () => {
    setSavingUnitConfig(true);
    
    try {
      if (!unitConfigFormData.name) {
        throw new Error('Please enter a configuration name');
      }

      if (unitConfigFormData.enable_cases && unitConfigFormData.units_per_case < 1) {
        throw new Error('Units per case must be at least 1');
      }

      if (unitConfigFormData.enable_pallets && unitConfigFormData.cases_per_pallet < 1) {
        throw new Error('Cases per pallet must be at least 1');
      }

      const configData = {
        company_id: companyId,
        name: unitConfigFormData.name,
        base_unit_name: unitConfigFormData.base_unit_name,
        enable_cases: unitConfigFormData.enable_cases,
        case_unit_name: unitConfigFormData.enable_cases ? unitConfigFormData.case_unit_name : null,
        units_per_case: unitConfigFormData.enable_cases ? parseInt(unitConfigFormData.units_per_case) : 1,
        enable_pallets: unitConfigFormData.enable_pallets,
        pallet_unit_name: unitConfigFormData.enable_pallets ? unitConfigFormData.pallet_unit_name : null,
        cases_per_pallet: unitConfigFormData.enable_pallets ? parseInt(unitConfigFormData.cases_per_pallet) : 1
      };

      if (editingUnitConfig) {
        const { error } = await supabase
          .from('unit_configurations')
          .update(configData)
          .eq('id', editingUnitConfig.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('unit_configurations')
          .insert([configData]);

        if (error) throw error;
      }

      handleCloseUnitConfigDialog();
      await fetchUnitConfigurations();
    } catch (err) {
      console.error('Error saving unit configuration:', err);
      setErrorMessage(err.message || 'Failed to save unit configuration');
    } finally {
      setSavingUnitConfig(false);
    }
  };

  const handleCloseUnitConfigDialog = () => {
    setUnitConfigDialogOpen(false);
    setEditingUnitConfig(null);
    setUnitConfigFormData({
      name: '',
      base_unit_name: 'units',
      enable_cases: true,
      case_unit_name: 'cases',
      units_per_case: 12,
      enable_pallets: true,
      pallet_unit_name: 'pallets',
      cases_per_pallet: 42
    });
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm.trim()) return products;
    
    const searchTerm = productSearchTerm.toLowerCase();
    return products.filter(product => 
      product.name?.toLowerCase().includes(searchTerm) ||
      product.description?.toLowerCase().includes(searchTerm) ||
      product.sku?.toLowerCase().includes(searchTerm)
    );
  };

  // Formulation dialog handlers
  const handleOpenFormulationSelection = () => {
    setFormulationSearchTerm('');
    setFormulationSelectionDialogOpen(true);
  };

  const handleCloseFormulationSelection = () => {
    setFormulationSelectionDialogOpen(false);
    setFormulationSearchTerm('');
  };

  const handleSelectFormulation = (formulation) => {
    setNewBatch(prev => ({ ...prev, formulation_id: formulation.id }));
    handleCloseFormulationSelection();
  };

  const getFilteredFormulations = () => {
    if (!formulationSearchTerm.trim()) return formulations;
    
    const searchTerm = formulationSearchTerm.toLowerCase();
    return formulations.filter(formulation => 
      formulation.name?.toLowerCase().includes(searchTerm) ||
      formulation.version?.toLowerCase().includes(searchTerm) ||
      formulation.description?.toLowerCase().includes(searchTerm)
    );
  };

  // Location dialog handlers
  const handleOpenLocationSelection = () => {
    setLocationSearchTerm('');
    setLocationSelectionDialogOpen(true);
  };

  const handleCloseLocationSelection = () => {
    setLocationSelectionDialogOpen(false);
    setLocationSearchTerm('');
  };

  const handleSelectLocation = (location) => {
    setNewBatch(prev => ({ ...prev, production_location: location.id }));
    handleCloseLocationSelection();
  };

  const getFilteredLocations = () => {
    if (!locationSearchTerm.trim()) return locations;
    
    const searchTerm = locationSearchTerm.toLowerCase();
    return locations.filter(location => 
      location.name?.toLowerCase().includes(searchTerm) ||
      location.facility_type?.toLowerCase().includes(searchTerm) ||
      location.city?.toLowerCase().includes(searchTerm) ||
      location.state?.toLowerCase().includes(searchTerm)
    );
  };

  const handleCreateBatch = async () => {
    setSaving(true);
    setErrorMessage('');
    
    try {
      // Validate required fields
      if (!newBatch.batch_number || !newBatch.lot_number || !newBatch.product_id || 
          !newBatch.formulation_id || !newBatch.production_location) {
        setErrorMessage('Please fill in all required fields: Batch Number, Lot Number, Product, Formulation, and Production Location.');
        return;
      }



      // Validate variant distribution if product has variants
      if (productVariants.length > 0) {
        if (!newBatch.variant_distribution || newBatch.variant_distribution.length === 0) {
          setErrorMessage('Please specify quantities for at least one product variant.');
          return;
        } else if (!newBatch.variant_distribution.some(v => v.quantity > 0)) {
          setErrorMessage('At least one variant must have a quantity greater than 0.');
          return;
        }
      }


      const batchData = {
        batch_number: newBatch.batch_number,
        lot_number: newBatch.lot_number,
        product_id: newBatch.product_id,
        formulation_id: newBatch.formulation_id,
        production_location: newBatch.production_location,
        production_line: newBatch.production_line,
        shift_supervisor: newBatch.shift_supervisor,
        planned_quantity: newBatch.planned_quantity,
        planned_unit_of_measure: newBatch.planned_unit_of_measure,
        status: newBatch.status,
        priority: newBatch.priority,
        notes: newBatch.notes,
        company_id: companyId,
        created_by: user?.id,
        updated_by: user?.id,
        // Convert datetime objects to proper format for database
        manufacture_date: newBatch.manufacture_date ? newBatch.manufacture_date.format('YYYY-MM-DD') : null,
        expiration_date: newBatch.expiration_date ? newBatch.expiration_date.format('YYYY-MM-DD') : null,
        planned_start_date: newBatch.planned_start_datetime ? newBatch.planned_start_datetime.format('YYYY-MM-DD') : null,
        planned_start_time: newBatch.planned_start_datetime ? newBatch.planned_start_datetime.format('HH:mm') : null,
        planned_completion_date: newBatch.planned_completion_datetime ? newBatch.planned_completion_datetime.format('YYYY-MM-DD') : null,
        planned_completion_time: newBatch.planned_completion_datetime ? newBatch.planned_completion_datetime.format('HH:mm') : null,
        variant_distribution: newBatch.variant_distribution || [],
        unit_configuration_id: newBatch.unit_configuration_id || null,
        product_variant_id: newBatch.product_variant_id || null,
        product_variant: newBatch.product_variant || null
      };

      let data, error;

      if (isEditing && editingBatch) {
        // Update existing batch
        const updateData = { ...batchData };
        delete updateData.created_by; // Don't update created_by
        
        const result = await supabase
          .from('batch_calendar')
          .update(updateData)
          .eq('id', editingBatch.id)
          .select();
        
        data = result.data;
        error = result.error;
      } else {
        // Create new batch
        const result = await supabase
          .from('batch_calendar')
          .insert([batchData])
          .select();
        
        data = result.data;
        error = result.error;
      }
      
      if (error) {
        if (error.code === '23505' && error.message.includes('unique_batch_calendar_per_company')) {
          throw new Error('Batch/Lot number combination already exists. Please use a different batch number or lot number.');
        }
        throw error;
      }
      
      setCreateDialogOpen(false);
      setNewBatch({
        batch_number: '',
        lot_number: '',
        product_id: '',
        formulation_id: '',
        production_location: '',
        manufacture_date: null,
        expiration_date: null,
        planned_start_datetime: null,
        planned_completion_datetime: null,
        status: 'planned',
        priority: 'normal',
        production_line: '',
        shift_supervisor: '',
        planned_quantity: 100,
        planned_unit_of_measure: 'g',
        variant_distribution: [],
        unit_configuration_id: '',
        product_variant_id: '',
        product_variant: ''
      });
      
      // Reset product variants and unit configuration
      setProductVariants([]);
      setSelectedUnitConfig(null);
      setUnitInputs({});
      
      // Reset editing state
      setEditingBatch(null);
      setIsEditing(false);
      
      // Calendar entry created successfully - refresh calendar data
      await fetchCalendarBatches();
    } catch (error) {
      console.error('Error creating batch calendar entry:', error);
      setErrorMessage(error.message || 'An error occurred while saving the batch. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Calendar view rendering functions
  const renderCalendarContent = () => {
    switch (currentView) {
      case 'day':
        return renderDayView();
      case 'week':
        return renderWeekView();
      case 'month':
        return renderMonthView();
      case 'year':
        return renderYearView();
      default:
        return renderMonthView();
    }
  };

  const renderDayView = () => {
    const dayStart = startOfDay(currentDate);
    const dayEnd = endOfDay(currentDate);
    const hours = eachHourOfInterval({ start: dayStart, end: dayEnd });
    
    const dayBatches = calendarBatches.filter(batch => 
      batch.planned_start_date && isSameDay(new Date(batch.planned_start_date + 'T00:00:00'), currentDate)
    );

    return (
      <Box sx={{ p: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr', maxHeight: '75vh', overflow: 'hidden' }}>
          {/* Time column */}
          <Box sx={{ pt: 1 }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 40,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  color: 'text.secondary'
                }}
              >
                {format(hour, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
              </Box>
            ))}
          </Box>
          
          {/* Batches column */}
          <Box sx={{ position: 'relative', pt: 1 }}>
            {hours.map((hour) => (
              <Box
                key={hour}
                sx={{
                  height: 40,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  cursor: 'pointer',
                  position: 'relative',
                  zIndex: 0,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    handleDateClick(hour, e);
                  }
                }}
              />
            ))}
            
            {/* Render batches */}
            {(() => {
              const batchesByHour = {};
              dayBatches.forEach((batch, originalIndex) => {
                const batchHour = batch.planned_start_time ? getHours(new Date(`2000-01-01T${batch.planned_start_time}`)) : 9;
                if (!batchesByHour[batchHour]) batchesByHour[batchHour] = [];
                batchesByHour[batchHour].push({...batch, originalIndex});
              });

              return Object.entries(batchesByHour)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([hour, hourBatches]) => {
                  hourBatches.sort((a, b) => a.originalIndex - b.originalIndex);
                  
                const top = (parseInt(hour) * 40) + 8;
                const containerElement = document.querySelector('[data-calendar-container]');
                let availableWidth;
                if (containerElement) {
                  const containerRect = containerElement.getBoundingClientRect();
                  availableWidth = Math.max(containerRect.width - 120, 400);
                } else {
                  availableWidth = Math.max(window.innerWidth - 200, 400);
                }
                
                let totalWidth = 0;
                let maxVisible = 0;
                
                for (let i = 0; i < hourBatches.length; i++) {
                  const batchWidth = Math.min(Math.max(hourBatches[i].batch_number?.length * 7 + 24 || 100, 80), 200);
                  const gapWidth = i > 0 ? 8 : 0; // Increased spacing from 6 to 8
                  const needsPlusButton = i < hourBatches.length - 1;
                  const plusButtonWidth = needsPlusButton ? 68 : 0; // Adjusted for new spacing
                  
                  if (totalWidth + batchWidth + gapWidth + plusButtonWidth <= availableWidth) {
                    totalWidth += batchWidth + gapWidth;
                    maxVisible++;
                  } else {
                    if (maxVisible > 0 && totalWidth + 68 <= availableWidth) {
                      break;
                    } else if (maxVisible === 0) {
                      maxVisible = 1;
                    }
                    break;
                  }
                }
                
                if (maxVisible >= hourBatches.length) {
                  maxVisible = hourBatches.length;
                }
                
                const visibleBatches = hourBatches.slice(0, maxVisible);
                const hiddenBatches = hourBatches.slice(maxVisible);
                const hiddenCount = hiddenBatches.length;

                return (
                  <Box key={hour} sx={{ position: 'absolute', top: `${top}px`, left: 8, right: 8, height: 40, pointerEvents: 'none' }}>
                    {visibleBatches.map((batch, index) => {
                      const batchWidth = Math.min(Math.max(batch.batch_number?.length * 7 + 24 || 100, 80), 200);
                      let leftPosition = 0;
                      
                      // Calculate position with proper spacing
                      for (let i = 0; i < index; i++) {
                        const prevBatchWidth = Math.min(Math.max(visibleBatches[i].batch_number?.length * 7 + 24 || 100, 80), 200);
                        leftPosition += prevBatchWidth + 8; // Increased spacing from 6 to 8
                      }
                      
                      return (
                        <Box
                          key={batch.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBatchClick(batch, e);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${leftPosition}px`,
                            width: `${batchWidth}px`,
                            height: 32,
                            backgroundColor: getStatusColor(batch.status),
                            color: 'white',
                            borderRadius: 2,
                            px: 1,
                            py: 0.5,
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            zIndex: 2,
                            overflow: 'hidden',
                            pointerEvents: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            gap: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 8px ${alpha(getStatusColor(batch.status), 0.4)}`
                            }
                          }}
                        >
                          <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {batch.batch_number}
                          </Box>
                        </Box>
                      );
                    })}
                    
                    {hiddenCount > 0 && (() => {
                      let buttonLeftPosition = 0;
                      for (let i = 0; i < visibleBatches.length; i++) {
                        const batchWidth = Math.min(Math.max(visibleBatches[i].batch_number?.length * 7 + 24 || 100, 80), 200);
                        buttonLeftPosition += batchWidth + 8; // Consistent spacing
                      }
                      
                      return (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDayBatches(hiddenBatches);
                            setSelectedDate(new Date());
                            setDayBatchesAnchor(e.currentTarget);
                            setDayBatchesOpen(true);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${buttonLeftPosition}px`,
                            width: 60,
                            height: 32,
                            backgroundColor: alpha(theme.palette.grey[500], 0.9),
                            color: 'white',
                            borderRadius: 2,
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            zIndex: 10,
                            pointerEvents: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              backgroundColor: alpha(theme.palette.grey[600], 0.9)
                            }
                          }}
                        >
                          +{hiddenCount}
                        </Box>
                      );
                    })()}
                  </Box>
                );
              });
            })()}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const hours = eachHourOfInterval({ 
      start: setHours(weekStart, 0), 
      end: setHours(weekStart, 23) 
    });

    return (
      <Box sx={{ p: 0 }}>
        {/* Week header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(7, 1fr)', borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}` }}>
          <Box />
          {weekDays.map((day) => (
            <Box
              key={day}
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                backgroundColor: isToday(day) ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: isToday(day) ? 'primary.main' : 'text.primary' }}>
                {format(day, 'EEE')}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, color: isToday(day) ? 'primary.main' : 'text.primary' }}>
                {format(day, 'd')}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Week grid */}
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(7, 1fr)' }}>
            {/* Time column */}
            <Box sx={{ pt: 1 }}>
              {hours.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    height: 50,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    color: 'text.secondary'
                  }}
                >
                  {format(hour, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a')}
                </Box>
              ))}
            </Box>
            
            {/* Day columns */}
            {weekDays.map((day) => (
              <Box key={day} sx={{ position: 'relative', pt: 1 }}>
                {hours.map((hour) => (
                  <Box
                    key={hour}
                    sx={{
                      height: 50,
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                      borderLeft: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: 0,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        handleDateClick(setHours(day, getHours(hour)), e);
                      }
                    }}
                  />
                ))}
                
                {/* Render batches for this day */}
                {(() => {
                  const dayBatches = calendarBatches.filter(batch => batch.planned_start_date && isSameDay(new Date(batch.planned_start_date + 'T00:00:00'), day));
                  const batchesByHour = {};
                  dayBatches.forEach((batch, originalIndex) => {
                    const batchHour = batch.planned_start_time ? getHours(new Date(`2000-01-01T${batch.planned_start_time}`)) : 9;
                    if (!batchesByHour[batchHour]) batchesByHour[batchHour] = [];
                    batchesByHour[batchHour].push({...batch, originalIndex});
                  });

                  return Object.entries(batchesByHour)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([hour, hourBatches]) => {
                      hourBatches.sort((a, b) => a.originalIndex - b.originalIndex);
                    const top = (parseInt(hour) * 50) + 8;
                    const maxVisible = 2; // Allow 2 batches to be visible vertically
                    const visibleBatches = hourBatches.slice(0, maxVisible);
                    const hiddenBatches = hourBatches.slice(maxVisible);
                    const hiddenCount = hiddenBatches.length;

                    return (
                      <Box key={`${day}-${hour}`} sx={{ position: 'absolute', top: `${top}px`, left: 2, right: 2, height: 50, pointerEvents: 'none' }}>
                        {visibleBatches.map((batch, index) => (
                          <Box
                            key={batch.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBatchClick(batch, e);
                            }}
                            sx={{
                              position: 'absolute',
                              top: index * 24, // Reduced spacing to fit better
                              left: 0,
                              right: hiddenCount > 0 && index === maxVisible - 1 ? 50 : 0,
                              height: 22, // Reduced height to prevent bleeding
                              backgroundColor: getStatusColor(batch.status),
                              color: 'white',
                              borderRadius: 2,
                              px: 1,
                              py: 0.5,
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              zIndex: 2,
                              pointerEvents: 'auto',
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 8px ${alpha(getStatusColor(batch.status), 0.4)}`
                              }
                            }}
                          >
                            {batch.batch_number}
                          </Box>
                        ))}
                        
                        {hiddenCount > 0 && (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              // Allow creating new batch in this time slot
                              handleDateClick(setHours(day, parseInt(hour)), e);
                            }}
                            sx={{
                              position: 'absolute',
                              top: (maxVisible - 1) * 24, // Consistent with new batch spacing
                              right: 0,
                              width: 45,
                              height: 22, // Match batch height
                              backgroundColor: alpha(theme.palette.grey[600], 0.8),
                              color: 'white',
                              borderRadius: 2,
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              zIndex: 3,
                              pointerEvents: 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                backgroundColor: alpha(theme.palette.grey[600], 0.9)
                              }
                            }}
                          >
                            +{hiddenCount}
                          </Box>
                        )}
                      </Box>
                    );
                  });
                })()}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderYearView = () => {
    const months = Array.from({ length: 12 }, (_, i) => new Date(currentDate.getFullYear(), i, 1));

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 3, width: '100%' }}>
          {months.map((month) => {
            const monthStart = startOfMonth(month);
            const monthEnd = endOfMonth(monthStart);
            const startDate = startOfWeek(monthStart);
            const endDate = endOfWeek(monthEnd);
            
            const monthBatches = calendarBatches.filter(batch => 
              batch.planned_start_date && 
              new Date(batch.planned_start_date + 'T00:00:00') >= monthStart && 
              new Date(batch.planned_start_date + 'T00:00:00') <= monthEnd
            );

            const weeks = [];
            let day = startDate;
            
            while (day <= endDate) {
              const week = [];
              for (let i = 0; i < 7; i++) {
                week.push(day);
                day = addDays(day, 1);
              }
              weeks.push(week);
            }

            return (
              <Box
                key={month}
                sx={{
                  border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  aspectRatio: '5 / 4',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: `0 1px 3px ${alpha(theme.palette.grey[400], 0.1)}`
                }}
              >
                <Box sx={{ 
                  py: 1.5, 
                  px: 1,
                  backgroundColor: alpha(theme.palette.grey[50], 0.8),
                  borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                  minHeight: 'auto'
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, textAlign: 'center', color: 'text.primary', fontSize: '0.875rem' }}>
                    {format(month, 'MMM')}
                  </Typography>
                </Box>
                
                <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Days header */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5, py: 0.5 }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dayName, index) => (
                      <Typography key={index} variant="caption" sx={{ textAlign: 'center', fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2, fontWeight: 500 }}>
                        {dayName}
                      </Typography>
                    ))}
                  </Box>
                  
                  {/* Calendar grid */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {weeks.map((week, weekIndex) => (
                      <Box key={weekIndex} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', flex: 1, mb: 0.25 }}>
                        {week.map((day) => {
                          const dayBatches = monthBatches.filter(batch => isSameDay(new Date(batch.planned_start_date + 'T00:00:00'), day));
                          
                          return (
                            <Box
                            key={day}
                            sx={{
                              minHeight: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              borderRadius: 1,
                              mx: 0.25,
                              my: 0.125,
                              backgroundColor: !isSameMonth(day, month) 
                                ? 'transparent'
                                : isToday(day) 
                                  ? alpha(theme.palette.primary.main, 0.15)
                                  : dayBatches.length > 0
                                    ? alpha(getStatusColor(dayBatches[0].status), 0.25)
                                    : 'transparent',
                              '&:hover': {
                                backgroundColor: !isSameMonth(day, month) 
                                  ? alpha(theme.palette.grey[300], 0.1)
                                  : alpha(theme.palette.primary.main, 0.08)
                              }
                            }}
                            onClick={(e) => handleDateClick(day, e)}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: '0.75rem',
                                fontWeight: isToday(day) ? 500 : 400,
                                color: !isSameMonth(day, month) 
                                  ? alpha(theme.palette.text.disabled, 0.6)
                                  : isToday(day) 
                                    ? 'primary.main' 
                                    : 'text.primary',
                                lineHeight: 1.2
                              }}
                            >
                              {format(day, 'd')}
                            </Typography>
                          </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const dateFormat = "d";
    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    // Generate calendar grid
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        const cloneDay = day;
        
        // Get batches for this day
        const dayBatches = calendarBatches.filter(batch => 
          batch.planned_start_date && isSameDay(new Date(batch.planned_start_date + 'T00:00:00'), day)
        );

        days.push(
          <Box
            key={day}
            sx={{
              minHeight: 160,
              height: '160px',
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              p: 1,
              cursor: 'pointer',
              backgroundColor: !isSameMonth(day, monthStart) 
                ? alpha(theme.palette.grey[100], 0.5)
                : isToday(day) 
                  ? alpha(theme.palette.primary.main, 0.08)
                  : 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
            onClick={(e) => handleDateClick(cloneDay, e)}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: isToday(day) ? 500 : 500,
                color: !isSameMonth(day, monthStart) 
                  ? 'text.disabled' 
                  : isToday(day) 
                    ? 'primary.main' 
                    : 'text.primary',
                mb: 1,
                fontSize: '0.9rem'
              }}
            >
              {formattedDate}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {dayBatches.slice(0, 2).map((batch) => (
                <Tooltip key={batch.id} title={batch.batch_number}>
                  <Chip
                    label={batch.batch_number}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBatchClick(batch, e);
                    }}
                    sx={{
                      height: 24,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      backgroundColor: getStatusColor(batch.status),
                      color: 'white',
                      borderRadius: 2,
                      cursor: 'pointer',
                      '& .MuiChip-label': {
                        px: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      },
                      '&:hover': {
                        backgroundColor: getStatusColor(batch.status),
                          opacity: 0.8
                      },
                      transition: 'all 0.2s ease'
                    }}
                  />
                </Tooltip>
              ))}
              
              {dayBatches.length > 2 && (
                <Chip
                  label={`+${dayBatches.length - 2} more`}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDateClick(cloneDay, e);
                  }}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    backgroundColor: alpha(theme.palette.grey[600], 0.8),
                    color: 'white',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      px: 1
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.grey[600], 0.9)
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              )}
            </Box>
          </Box>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <Box key={day} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {days}
        </Box>
      );
      days = [];
    }

    return (
      <Box sx={{ p: 0 }}>
        {rows}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
          <CalendarContainer data-calendar-container sx={{ width: '100%', minHeight: '80vh', mt: 4 }}>
        {/* Calendar Header */}
        <Box sx={{ 
          p: 3, 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              fontSize: '1.5rem'
            }}>
              {getViewTitle()}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TodayIcon />}
                onClick={goToToday}
                sx={{
                  borderRadius: 2,
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Today
              </Button>
              
              {/* View Selector */}
              <FormControl size="small" sx={{ minWidth: 120, ml: 2 }}>
                <InputLabel>View</InputLabel>
                <Select
                  value={currentView}
                  label="View"
                  onChange={(e) => setCurrentView(e.target.value)}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        minWidth: 120,
                        contain: 'layout style paint',
                        zIndex: 9999
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[400], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="day">Day</MenuItem>
                  <MenuItem value="week">Week</MenuItem>
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="year">Year</MenuItem>
                </Select>
              </FormControl>
              
              {/* Time Format Selector */}
              <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
                <InputLabel sx={{ fontSize: '0.9rem' }}>Time</InputLabel>
                <Select
                  value={timeFormat}
                  label="Time"
                  onChange={(e) => updateTimeFormat(e.target.value)}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        minWidth: 120,
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[400], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="12hour">12 Hour</MenuItem>
                  <MenuItem value="24hour">24 Hour</MenuItem>
                </Select>
              </FormControl>
              
              <IconButton 
                onClick={navigatePrev}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main
                  },
                  transition: 'all 0.2s ease',
                  ml: 2
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              
              <IconButton 
                onClick={navigateNext}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Days of Week Header - Only for Month View */}
          {currentView === 'month' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <Box
                key={day}
                sx={{
                  p: 1.5,
                  textAlign: 'center',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }}
              >
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 500, 
                  color: 'primary.main',
                  fontSize: '0.875rem'
                }}>
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
          )}
        </Box>

        {/* Calendar Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {renderCalendarContent()}
        </Box>

        {/* Legend */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 3, 
          p: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.grey[100], 0.4)})`,
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
        }}>
          {Object.entries(statusColors).map(([status, color]) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  backgroundColor: color,
                  borderRadius: '50%',
                  border: `2px solid ${alpha(color, 0.3)}`,
                  boxShadow: `0 2px 8px ${alpha(color, 0.3)}`
                }}
              />
              <Typography variant="caption" sx={{ 
                textTransform: 'capitalize',
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.8rem'
              }}>
                {status}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Day Batches Popover */}
        <Popover
          open={dayBatchesOpen}
          anchorEl={dayBatchesAnchor}
          onClose={handleDayBatchesClose}
          disableScrollLock={true}
          disablePortal={false}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              position: 'absolute !important',
              minWidth: 300,
              maxWidth: 400,
              borderRadius: 2,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              contain: 'layout style paint',
              zIndex: 9999
            }
          }}
          sx={{
            zIndex: 9999
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
              {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            
            <List sx={{ py: 0 }}>
              {selectedDayBatches.map((batch, index) => (
                <Box key={batch.id}>
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 1,
                      cursor: 'pointer',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[100], 0.5)
                      }
                    }}
                    onClick={() => {
                      handleDayBatchesClose();
                      handleBatchClick(batch);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: getStatusColor(batch.status),
                          borderRadius: '50%',
                          flexShrink: 0
                        }}
                      />
                      <ListItemText 
                        primary={batch.batch_number}
                        secondary={batch.planned_start_time ? format(new Date(`2000-01-01T${batch.planned_start_time}`), timeFormat === '24hour' ? 'HH:mm' : 'h:mm a') : ''}
                        primaryTypographyProps={{
                          sx: { fontSize: '0.9rem', fontWeight: 500 }
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: 'text.secondary' }
                        }}
                      />
                    </Box>
                  </ListItem>
                  {index < selectedDayBatches.length - 1 && <Divider sx={{ my: 0.5 }} />}
                </Box>
              ))}
            </List>
            
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Typography sx={{ fontSize: '1.2rem', fontWeight: 500 }}>+</Typography>}
              onClick={handleCreateFromPopover}
              sx={{
                mt: 2,
                borderRadius: 2,
                fontWeight: 500,
                color: 'primary.main',
                borderColor: alpha(theme.palette.primary.main, 0.5),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              Create
            </Button>
          </Box>
        </Popover>

      {/* Create Batch Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
            {isEditing ? 'Edit Production Batch' : 'Plan New Production Batch'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: 3,
          maxHeight: '80vh', 
          overflowY: 'auto',
          backdropFilter: 'blur(10px)',
          mt: 3
        }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3, color: 'black' }} onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3}>
              {/* BASIC INFORMATION */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Batch Number *"
                        value={newBatch.batch_number}
                        onChange={(e) => setNewBatch(prev => ({ ...prev, batch_number: e.target.value }))}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Lot Number *"
                        value={newBatch.lot_number}
                        onChange={(e) => setNewBatch(prev => ({ ...prev, lot_number: e.target.value }))}
                        required
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* PRODUCT & FORMULATION */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Product & Formulation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Product *"
                        value={newBatch.product_id ? products.find(p => p.id === newBatch.product_id)?.name || '' : ''}
                        placeholder="Click to select product..."
                        onClick={handleOpenProductSelection}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          '& .MuiOutlinedInput-root': {
                            cursor: 'pointer'
                          },
                          '& .MuiInputBase-input': {
                            cursor: 'pointer'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Formulation *"
                        value={newBatch.formulation_id ? 
                          (() => {
                            const formulation = formulations.find(f => f.id === newBatch.formulation_id);
                            return formulation ? `${formulation.name} v${formulation.version}` : '';
                          })() : ''
                        }
                        placeholder="Click to select formulation..."
                        onClick={handleOpenFormulationSelection}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          '& .MuiOutlinedInput-root': {
                            cursor: 'pointer'
                          },
                          '& .MuiInputBase-input': {
                            cursor: 'pointer'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* UNIT CONFIGURATION SELECTION */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Production Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {unitConfigurations.length === 0 ? (
                        <Box sx={{
                          textAlign: 'center',
                          py: 6,
                          px: 4,
                          border: '2px dashed',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[50], 0.5)
                        }}>
                          <SettingsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                            No Unit Configurations Found
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 300, mx: 'auto' }}>
                            Create unit configurations to define packaging structures.
                          </Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          {unitConfigurations.map((config) => {
                            const isSelected = selectedUnitConfig?.id === config.id;

                            return (
                              <Grid item xs={12} sm={6} md={4} key={config.id}>
                                <Card sx={{ 
                                  borderRadius: 3, 
                                  border: `2px solid ${isSelected ? theme.palette.primary.main : alpha(theme.palette.grey[400], 0.8)}`,
                                  background: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'white',
                                  cursor: 'pointer',
                                  height: 280,
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    border: `2px solid ${theme.palette.primary.main}`
                                  }
                                }}
                                onClick={() => {
                                  setSelectedUnitConfig(config);
                                  setNewBatch(prev => ({ ...prev, unit_configuration_id: config.id }));
                                  setUnitInputs({});
                                }}
                                >
                                  <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem', mb: 2 }}>
                                      {config.name}
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.85rem' }}>
                                      {config.enable_cases && !config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name}`}
                                      {config.enable_cases && config.enable_pallets && `${config.base_unit_name} → ${config.case_unit_name} → ${config.pallet_unit_name}`}
                                      {!config.enable_cases && !config.enable_pallets && `${config.base_unit_name} only`}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                      {config.enable_cases && (
                                        <Chip 
                                          label="Cases" 
                                          size="small" 
                                          sx={{
                                            bgcolor: alpha(theme.palette.info.main, 0.1),
                                            color: 'info.main',
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            height: 24
                                          }}
                                        />
                                      )}
                                      {config.enable_pallets && (
                                        <Chip 
                                          label="Pallets" 
                                          size="small" 
                                          sx={{
                                            bgcolor: alpha(theme.palette.success.main, 0.1),
                                            color: 'success.main',
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            height: 24
                                          }}
                                        />
                                      )}
                                    </Box>

                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                          Units/Case
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                          {config.enable_cases ? config.units_per_case : 'N/A'}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                          Cases/Pallet
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                          {config.enable_pallets ? config.cases_per_pallet : 'N/A'}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                          Total/Pallet
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '0.95rem' }}>
                                          {config.enable_pallets && config.enable_cases
                                            ? (config.units_per_case * config.cases_per_pallet).toLocaleString()
                                            : config.enable_cases
                                            ? `${config.units_per_case}/case`
                                            : '1'
                                          }
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setUnitConfigDialogOpen(true)}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.2,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            bgcolor: 'primary.main',
                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              bgcolor: 'primary.dark',
                              transform: 'translateY(-1px)',
                              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                            }
                          }}
                        >
                          Add Configuration
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* VARIANT DISTRIBUTION */}
              {selectedUnitConfig && productVariants.length > 0 && (newBatch.product_variant_id || (newBatch.variant_distribution && newBatch.variant_distribution.length > 0)) ? (
                productVariants.filter(variant => {
                  // If single variant mode, filter by product_variant_id
                  if (newBatch.product_variant_id) {
                    return variant.id === newBatch.product_variant_id;
                  }
                  // If multi-variant mode, show all variants that are in the distribution
                  return newBatch.variant_distribution && newBatch.variant_distribution.some(d => d.variant_id === variant.id);
                }).map((variant, index) => (
                  <Grid item xs={12} key={variant.id}>
                    <Card sx={{ 
                      p: 4, 
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      transition: 'all 0.3s ease',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        borderColor: theme.palette.primary.main
                      }
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                        {variant.volume_weight}{variant.volume_unit} Production
                        {variant.sku && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500, ml: 2 }}>
                            (SKU: {variant.sku})
                          </Typography>
                        )}
                      </Typography>

                      <Grid container spacing={3}>
                        {selectedUnitConfig.enable_pallets && (
                          <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label={`${(selectedUnitConfig.pallet_unit_name || 'Pallets').charAt(0).toUpperCase() + (selectedUnitConfig.pallet_unit_name || 'pallets').slice(1)} to Make`}
                            type="number"
                            value={unitInputs[variant.id]?.pallets || ''}
                            onChange={(e) => {
                              const newPallets = parseInt(e.target.value) || 0;
                              const newInputs = { ...(unitInputs[variant.id] || {}), pallets: newPallets };
                              setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                              updateVariantFromUnitInputs(variant.id, newInputs);
                            }}
                            inputProps={{ min: 0, step: 1 }}
                            placeholder="0"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  backdropFilter: 'blur(10px)',
                                  '& fieldset': {
                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main
                                  }
                                }
                              }}
                            />
                          </Grid>
                        )}

                        {selectedUnitConfig.enable_cases && (
                          <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label={`${(selectedUnitConfig.case_unit_name || 'Cases').charAt(0).toUpperCase() + (selectedUnitConfig.case_unit_name || 'cases').slice(1)} to Make`}
                            type="number"
                            value={unitInputs[variant.id]?.cases || ''}
                            onChange={(e) => {
                              const newCases = parseInt(e.target.value) || 0;
                              const newInputs = { ...(unitInputs[variant.id] || {}), cases: newCases };
                              setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                              updateVariantFromUnitInputs(variant.id, newInputs);
                            }}
                            inputProps={{ min: 0, step: 1 }}
                            placeholder="0"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  backdropFilter: 'blur(10px)',
                                  '& fieldset': {
                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main
                                  }
                                }
                              }}
                            />
                          </Grid>
                        )}

                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label={`${(selectedUnitConfig.base_unit_name || 'Units').charAt(0).toUpperCase() + (selectedUnitConfig.base_unit_name || 'units').slice(1)} to Make`}
                            type="number"
                            value={unitInputs[variant.id]?.units || ''}
                            onChange={(e) => {
                              const newUnits = parseInt(e.target.value) || 0;
                              const newInputs = { ...(unitInputs[variant.id] || {}), units: newUnits };
                              setUnitInputs(prev => ({ ...prev, [variant.id]: newInputs }));
                              updateVariantFromUnitInputs(variant.id, newInputs);
                            }}
                            inputProps={{ min: 0, step: 1 }}
                            placeholder="0"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(10px)',
                                '& fieldset': {
                                  borderColor: alpha(theme.palette.primary.main, 0.3),
                                  borderWidth: 1
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main
                                }
                              }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Box sx={{ 
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            p: 3,
                            border: '1px solid #e0e0e0',
                            textAlign: 'center'
                          }}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 1 }}>
                              Total: {calculateTotalUnitsFromInputs(variant.id)} {selectedUnitConfig.base_unit_name || 'units'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              = {(calculateTotalUnitsFromInputs(variant.id) * variant.volume_weight).toLocaleString()} {variant.volume_unit} production volume
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                ))
              ) : !selectedUnitConfig ? (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                    Please select a unit configuration first.
                  </Typography>
                </Grid>
              ) : productVariants.length === 0 ? (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                    No variants available for this product. Please add variants first.
                  </Typography>
                </Grid>
              ) : null}

              {/* PRODUCTION DETAILS */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Production Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Production Location *"
                        value={newBatch.production_location ? locations.find(l => l.id === newBatch.production_location)?.name || '' : ''}
                        placeholder="Click to select location..."
                        onClick={handleOpenLocationSelection}
                        InputProps={{
                          readOnly: true,
                          sx: {
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          '& .MuiOutlinedInput-root': {
                            cursor: 'pointer'
                          },
                          '& .MuiInputBase-input': {
                            cursor: 'pointer'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Production Line"
                        value={newBatch.production_line}
                        onChange={(e) => setNewBatch(prev => ({ ...prev, production_line: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Shift Supervisor"
                        value={newBatch.shift_supervisor}
                        onChange={(e) => setNewBatch(prev => ({ ...prev, shift_supervisor: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={newBatch.status}
                          label="Status"
                          onChange={(e) => setNewBatch(prev => ({ ...prev, status: e.target.value }))}
                        >
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="planned">Planned</MenuItem>
                          <MenuItem value="scheduled">Scheduled</MenuItem>
                          <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={newBatch.priority}
                          label="Priority"
                          onChange={(e) => setNewBatch(prev => ({ ...prev, priority: e.target.value }))}
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="urgent">Urgent</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                  </Grid>
                </Card>
              </Grid>

              {/* DATES & TIMING */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Schedule & Dates
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Manufacture Date *"
                        value={newBatch.manufacture_date}
                        onChange={(date) => setNewBatch(prev => ({ ...prev, manufacture_date: date }))}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Expiration Date *"
                        value={newBatch.expiration_date}
                        onChange={(date) => setNewBatch(prev => ({ ...prev, expiration_date: date }))}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Planned Start Date & Time *"
                        value={newBatch.planned_start_datetime}
                        onChange={(datetime) => setNewBatch(prev => ({ ...prev, planned_start_datetime: datetime }))}
                        ampm={timeFormat !== '24hour'}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Planned Completion Date & Time *"
                        value={newBatch.planned_completion_datetime}
                        onChange={(datetime) => setNewBatch(prev => ({ ...prev, planned_completion_datetime: datetime }))}
                        ampm={timeFormat !== '24hour'}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => {
              setCreateDialogOpen(false);
              setEditingBatch(null);
              setIsEditing(false);
              setErrorMessage('');
              // Always reset variants and unit config when closing
              setProductVariants([]);
              setSelectedUnitConfig(null);
              setUnitInputs({});
            }}
            startIcon={<CloseIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          
          {isEditing && canDeletePage('production_calendar') && (
            <Button 
              onClick={handleDeleteBatch}
              variant="contained"
              color="error"
              startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              disabled={deleting || saving}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 500,
                '&:disabled': {
                  background: theme.palette.grey[400],
                  color: theme.palette.grey[100]
                }
              }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          
          {canEditPage('production_calendar') && (
            <Button 
              onClick={handleCreateBatch}
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={saving || deleting}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: 'white',
                fontWeight: 500,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                },
                '&:disabled': {
                  background: theme.palette.grey[400],
                  color: theme.palette.grey[100]
                }
              }}
            >
              {isEditing ? 'Update Batch' : 'Plan Batch'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Custom Date Range Dialog */}
      <Dialog 
        open={dateRangeDialogOpen} 
        onClose={() => setDateRangeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Select Custom Date Range
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: 3,
          pt: 4,
          backdropFilter: 'blur(10px)'
        }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontWeight: 500
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={6}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  minDate={startDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontWeight: 500
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.9rem',
                          fontWeight: 500
                        }
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const today = dayjs();
                      setStartDate(today);
                      setEndDate(today.add(7, 'day'));
                    }}
                    sx={{ fontWeight: 500 }}
                  >
                    Next 7 Days
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const today = dayjs();
                      setStartDate(today);
                      setEndDate(today.add(30, 'day'));
                    }}
                    sx={{ fontWeight: 500 }}
                  >
                    Next 30 Days
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const today = dayjs();
                      setStartDate(today.startOf('month'));
                      setEndDate(today.endOf('month'));
                    }}
                    sx={{ fontWeight: 500 }}
                  >
                    This Month
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const nextMonth = dayjs().add(1, 'month');
                      setStartDate(nextMonth.startOf('month'));
                      setEndDate(nextMonth.endOf('month'));
                    }}
                    sx={{ fontWeight: 500 }}
                  >
                    Next Month
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => {
              setStartDate(null);
              setEndDate(null);
              setDateRangeDialogOpen(false);
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Clear Range
          </Button>
          <Button 
            onClick={() => setDateRangeDialogOpen(false)}
            variant="contained"
            disabled={!startDate || !endDate}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            Apply Range
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unit Configuration Dialog */}
      <Dialog
        open={unitConfigDialogOpen}
        onClose={handleCloseUnitConfigDialog}
        maxWidth="lg"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: { 
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {editingUnitConfig ? 'Edit Configuration' : 'Add Configuration'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                mb: 3,
                mt: 4
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  mb: 3,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  <SettingsIcon sx={{ fontSize: 28 }} />
                  Basic Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="medium"
                      label="Configuration Name"
                      value={unitConfigFormData.name}
                      onChange={(e) => setUnitConfigFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Standard Bottles, Large Cases, Individual Units"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="medium"
                      label="Base Unit Name"
                      value={unitConfigFormData.base_unit_name}
                      onChange={(e) => setUnitConfigFormData(prev => ({ ...prev, base_unit_name: e.target.value }))}
                      placeholder="e.g., units, bottles, pieces"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          '& fieldset': {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 1
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                mb: 3
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  mb: 3,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  <Inventory2Icon sx={{ fontSize: 28 }} />
                  Packaging Structure
                </Typography>
                
                {/* Cases Configuration */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={unitConfigFormData.enable_cases}
                          onChange={(e) => setUnitConfigFormData(prev => ({ 
                            ...prev, 
                            enable_cases: e.target.checked,
                            enable_pallets: e.target.checked ? unitConfigFormData.enable_pallets : false
                          }))}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          Enable Cases
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      Group individual units into cases for easier handling
                    </Typography>
                  </Grid>
                  
                  {unitConfigFormData.enable_cases && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Case Unit Name"
                          value={unitConfigFormData.case_unit_name}
                          onChange={(e) => setUnitConfigFormData(prev => ({ ...prev, case_unit_name: e.target.value }))}
                          placeholder="e.g., cases, boxes, packs"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Units per Case"
                          type="number"
                          value={unitConfigFormData.units_per_case}
                          onChange={(e) => setUnitConfigFormData(prev => ({ ...prev, units_per_case: parseInt(e.target.value) || 1 }))}
                          InputProps={{
                            inputProps: { min: 1 }
                          }}
                          helperText="How many individual units make up one case"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Pallets Configuration */}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={unitConfigFormData.enable_pallets}
                          onChange={(e) => setUnitConfigFormData(prev => ({ ...prev, enable_pallets: e.target.checked }))}
                          color="primary"
                          disabled={!unitConfigFormData.enable_cases}
                        />
                      }
                      label={
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          Enable Pallets
                        </Typography>
                      }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      Group cases into pallets for bulk storage {!unitConfigFormData.enable_cases && '(requires cases to be enabled)'}
                    </Typography>
                  </Grid>
                  
                  {unitConfigFormData.enable_pallets && unitConfigFormData.enable_cases && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Pallet Unit Name"
                          value={unitConfigFormData.pallet_unit_name}
                          onChange={(e) => setUnitConfigFormData(prev => ({ ...prev, pallet_unit_name: e.target.value }))}
                          placeholder="e.g., pallets, skids"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Cases per Pallet"
                          type="number"
                          value={unitConfigFormData.cases_per_pallet}
                          onChange={(e) => setUnitConfigFormData(prev => ({ ...prev, cases_per_pallet: parseInt(e.target.value) || 1 }))}
                          InputProps={{
                            inputProps: { min: 1 }
                          }}
                          helperText="How many cases fit on one pallet"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '& fieldset': {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                borderWidth: 1
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Quick Presets */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    Quick Presets
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData(prev => ({ ...prev, enable_cases: false, enable_pallets: false }))}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Units Only
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData(prev => ({ ...prev, enable_cases: true, enable_pallets: false, units_per_case: 12 }))}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Cases (12 units)
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData(prev => ({ ...prev, enable_cases: true, enable_pallets: true, units_per_case: 12, cases_per_pallet: 42 }))}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Full (12×42)
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="medium"
                      onClick={() => setUnitConfigFormData(prev => ({ ...prev, enable_cases: true, enable_pallets: true, units_per_case: 24, cases_per_pallet: 48 }))}
                      sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                    >
                      Large (24×48)
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={handleCloseUnitConfigDialog} 
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUnitConfigSubmit}
            disabled={savingUnitConfig || !unitConfigFormData.name}
            startIcon={savingUnitConfig ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {savingUnitConfig ? 'Saving...' : (editingUnitConfig ? 'Update Configuration' : 'Create Configuration')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog
        open={productSelectionDialogOpen}
        onClose={handleCloseProductSelection}
        maxWidth="lg"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            height: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          Select Product
          <IconButton 
            onClick={handleCloseProductSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {!dialogSelectedProduct ? (
            <>
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                <TextField
                  fullWidth
                  placeholder="Search products by name, description, or SKU..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                />
              </Box>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 500, 
                        fontSize: '0.9rem',
                        background: alpha(theme.palette.primary.main, 0.05),
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                      }}>
                        Product
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500, 
                        fontSize: '0.9rem',
                        background: alpha(theme.palette.primary.main, 0.05),
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        width: 100
                      }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredProducts().length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            {productSearchTerm ? 'No products found matching your search.' : 'No products available.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      getFilteredProducts().map((product) => (
                        <TableRow
                          key={product.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                {product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                SKU: {product.sku || 'No SKU'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={async () => {
                                await handleSelectProduct(product);
                              }}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 0.5,
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                }
                              }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box sx={{ p: 3 }}>
              {/* Selected Product Display */}
              <Box sx={{ 
                p: 3, 
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                mb: 3
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                  {dialogSelectedProduct.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  SKU: {dialogSelectedProduct.sku || 'No SKU'}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setDialogSelectedProduct(null);
                    setProductVariants([]);
                  }}
                  sx={{ 
                    ml: 2, 
                    fontSize: '0.8rem',
                    borderRadius: 2
                  }}
                >
                  Change Product
                </Button>
              </Box>

              {/* Variant Selection */}
              {productVariants && productVariants.length > 0 ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                    Select Variant:
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Variant</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productVariants.map((variant) => (
                        <TableRow
                          key={variant.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                {variant.name || `${variant.volume_weight}${variant.volume_unit}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                SKU: {variant.sku || 'No SKU'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={async () => {
                                await handleVariantSelection(variant);
                              }}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 0.5,
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                }
                              }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    No variants available for this product.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleConfirmProductSelection}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 500,
                      px: 3,
                      py: 1
                    }}
                  >
                    Use Product Without Variant
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
          {!dialogSelectedProduct && (
            <Button
              onClick={() => window.open(`/company/${companyId}/products`, '_blank')}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                fontSize: '0.9rem',
                textTransform: 'none',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }}
            >
              Add Product
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseProductSelection}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontSize: '0.9rem',
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmProductSelection}
              variant="contained"
              disabled={!dialogSelectedProduct || (productVariants?.length > 0)}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontSize: '0.9rem',
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                },
                '&:disabled': {
                  background: alpha(theme.palette.grey[400], 0.5),
                  color: alpha(theme.palette.grey[600], 0.7)
                }
              }}
            >
              {productVariants?.length > 0 ? 'Select Variant Above' : 'Confirm Selection'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Formulation Selection Dialog */}
      <Dialog
        open={formulationSelectionDialogOpen}
        onClose={handleCloseFormulationSelection}
        maxWidth="lg"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            height: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          Select Formulation
          <IconButton 
            onClick={handleCloseFormulationSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search formulations by name, version, or description..."
              value={formulationSearchTerm}
              onChange={(e) => setFormulationSearchTerm(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  fontSize: '0.9rem',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Formulation
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Base Size
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    width: 100
                  }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredFormulations().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {formulationSearchTerm ? 'No formulations found matching your search.' : 'No formulations available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredFormulations().map((formulation) => (
                    <TableRow
                      key={formulation.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => handleSelectFormulation(formulation)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {formulation.name} v{formulation.version}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Version: {formulation.version}
                          </Typography>
                        </Box>
                      </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {formulation.target_batch_size ? `${formulation.target_batch_size} ${formulation.unit_of_measure || 'g'}` : 'No base size'}
                          </Typography>
                        </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectFormulation(formulation);
                          }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
          <Button 
            onClick={handleCloseFormulationSelection}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => window.open(`/company/${companyId}/formulations`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
              }
            }}
          >
            Add Formulation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Selection Dialog */}
      <Dialog
        open={locationSelectionDialogOpen}
        onClose={handleCloseLocationSelection}
        maxWidth="lg"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            height: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          Select Production Location
          <IconButton 
            onClick={handleCloseLocationSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search locations by name, type, city, or state..."
              value={locationSearchTerm}
              onChange={(e) => setLocationSearchTerm(e.target.value)}
              InputProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  fontSize: '0.9rem',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Location
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Type & Address
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    width: 100
                  }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredLocations().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {locationSearchTerm ? 'No locations found matching your search.' : 'No locations available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredLocations().map((location) => (
                    <TableRow
                      key={location.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => handleSelectLocation(location)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {location.name}
                          </Typography>
                          {location.facility_type && (
                            <Typography variant="caption" color="text.secondary">
                              Type: {location.facility_type}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {[location.city, location.state].filter(Boolean).join(', ') || 'No address'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectLocation(location);
                          }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
          <Button 
            onClick={handleCloseLocationSelection}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => window.open(`/company/${companyId}/locations`, '_blank')}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
              }
            }}
          >
            Add Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Batch Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => {
          setViewDialogOpen(false);
          setViewingBatch(null);
        }}
        maxWidth="md"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Batch Details
          </Typography>
          <IconButton 
            onClick={() => {
              setViewDialogOpen(false);
              setViewingBatch(null);
            }}
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          backgroundColor: alpha(theme.palette.grey[50], 0.5), 
          p: 3,
          pt: 4,
          maxHeight: '70vh', 
          overflowY: 'auto',
          backdropFilter: 'blur(10px)',
          mt: 3
        }}>
          {viewingBatch && (
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Batch Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingBatch.batch_number || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Lot Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingBatch.lot_number || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip 
                            label={viewingBatch.status?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                            size="small"
                            sx={{ 
                              fontWeight: 500,
                              backgroundColor: getStatusColor(viewingBatch.status),
                              color: 'white'
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Priority
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip 
                            label={viewingBatch.priority?.toUpperCase() || 'NORMAL'}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Product & Formulation */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Product & Formulation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Product
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {products.find(p => p.id === viewingBatch.product_id)?.name || 'N/A'}
                        </Typography>
                        {viewingBatch.product_variant && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Variant: {viewingBatch.product_variant}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Formulation
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {(() => {
                            const formulation = formulations.find(f => f.id === viewingBatch.formulation_id);
                            return formulation ? `${formulation.name} v${formulation.version}` : 'N/A';
                          })()}
                        </Typography>
                      </Box>
                    </Grid>
                    {viewingBatch.unit_configuration_id && (
                      <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Unit Configuration
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {(() => {
                              const unitConfig = unitConfigurations.find(c => c.id === viewingBatch.unit_configuration_id);
                              return unitConfig ? `${unitConfig.name} (${getConfigDescription(unitConfig)})` : 'N/A';
                            })()}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>

              {/* Production Quantities */}
              {(viewingBatch.unit_configuration_id && viewingBatch.variant_distribution && viewingBatch.variant_distribution.length > 0) && (
                <Grid item xs={12}>
                  <Card sx={{ 
                    p: 3, 
                    border: `1px solid ${theme.palette.grey[400]}`, 
                    borderRadius: 3, 
                    backgroundColor: 'white',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary }}>
                      Production Quantities
                    </Typography>
                    
                    {(() => {
                      const unitConfig = unitConfigurations.find(c => c.id === viewingBatch.unit_configuration_id);
                      
                      if (!unitConfig) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Unit configuration not found
                          </Typography>
                        );
                      }
                      
                      return (
                        <Grid container spacing={3}>
                          {viewingBatch.variant_distribution.map((variantDist) => {
                            const variant = productVariants.find(v => v.id === variantDist.variant_id);
                            
                            if (!variant) return null;
                            
                            const totalUnits = variantDist.quantity || 0;
                            
                            // Calculate breakdown from total units
                            let remainingUnits = totalUnits;
                            let pallets = 0;
                            let cases = 0;
                            let units = 0;
                            
                            if (unitConfig.enable_pallets && unitConfig.enable_cases) {
                              const unitsPerPallet = unitConfig.cases_per_pallet * unitConfig.units_per_case;
                              pallets = Math.floor(remainingUnits / unitsPerPallet);
                              remainingUnits = remainingUnits % unitsPerPallet;
                            }
                            
                            if (unitConfig.enable_cases) {
                              cases = Math.floor(remainingUnits / unitConfig.units_per_case);
                              remainingUnits = remainingUnits % unitConfig.units_per_case;
                            }
                            
                            units = remainingUnits;
                            
                            return (
                              <Grid item xs={12} key={variant.id}>
                                <Box sx={{ 
                                  p: 3, 
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  borderRadius: 2,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.02)})`
                                }}>
                                  <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                                    {variant.volume_weight}{variant.volume_unit}
                                    {variant.sku && (
                                      <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 500, ml: 2 }}>
                                        (SKU: {variant.sku})
                                      </Typography>
                                    )}
                                  </Typography>

                                  <Grid container spacing={2}>
                                    {unitConfig.enable_pallets && pallets > 0 && (
                                      <Grid item xs={12} sm={4}>
                                        <Box sx={{ 
                                          p: 2, 
                                          borderRadius: 2, 
                                          bgcolor: alpha(theme.palette.success.main, 0.1),
                                          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                        }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {unitConfig.pallet_unit_name || 'Pallets'}
                                          </Typography>
                                          <Typography variant="h5" sx={{ fontWeight: 500, color: 'success.main' }}>
                                            {pallets.toLocaleString()}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    )}

                                    {unitConfig.enable_cases && cases > 0 && (
                                      <Grid item xs={12} sm={4}>
                                        <Box sx={{ 
                                          p: 2, 
                                          borderRadius: 2, 
                                          bgcolor: alpha(theme.palette.info.main, 0.1),
                                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                                        }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {unitConfig.case_unit_name || 'Cases'}
                                          </Typography>
                                          <Typography variant="h5" sx={{ fontWeight: 500, color: 'info.main' }}>
                                            {cases.toLocaleString()}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    )}

                                    {units > 0 && (
                                      <Grid item xs={12} sm={4}>
                                        <Box sx={{ 
                                          p: 2, 
                                          borderRadius: 2, 
                                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                                          border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                                        }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {unitConfig.base_unit_name || 'Units'}
                                          </Typography>
                                          <Typography variant="h5" sx={{ fontWeight: 500, color: 'warning.main' }}>
                                            {units.toLocaleString()}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    )}

                                    <Grid item xs={12}>
                                      <Divider sx={{ my: 1 }} />
                                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                          Total: {totalUnits.toLocaleString()} {unitConfig.base_unit_name || 'units'} = {(totalUnits * variant.volume_weight).toLocaleString()} {variant.volume_unit}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      );
                    })()}
                  </Card>
                </Grid>
              )}

              {/* Production Details */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Production Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Production Location
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {locations.find(l => l.id === viewingBatch.production_location)?.name || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Production Line
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingBatch.production_line || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Shift Supervisor
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingBatch.shift_supervisor || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Schedule Information */}
              <Grid item xs={12}>
                <Card sx={{ 
                  p: 3, 
                  border: `1px solid ${theme.palette.grey[400]}`, 
                  borderRadius: 3, 
                  backgroundColor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                    Schedule & Dates
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Planned Start
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingBatch.planned_start_date && viewingBatch.planned_start_time ? 
                            `${dayjs(viewingBatch.planned_start_date).format('MMM D, YYYY')} at ${dayjs(`2000-01-01T${viewingBatch.planned_start_time}`).format(timeFormat === '24hour' ? 'HH:mm' : 'h:mm A')}` : 
                            'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Planned Completion
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingBatch.planned_completion_date && viewingBatch.planned_completion_time ? 
                            `${dayjs(viewingBatch.planned_completion_date).format('MMM D, YYYY')} at ${dayjs(`2000-01-01T${viewingBatch.planned_completion_time}`).format(timeFormat === '24hour' ? 'HH:mm' : 'h:mm A')}` : 
                            'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Manufacture Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingBatch.manufacture_date ? dayjs(viewingBatch.manufacture_date).format('MMM D, YYYY') : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Expiration Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewingBatch.expiration_date ? dayjs(viewingBatch.expiration_date).format('MMM D, YYYY') : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => {
              setViewDialogOpen(false);
              setViewingBatch(null);
            }}
            startIcon={<CloseIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Close
          </Button>
          
          {canEditPage('production_calendar') && (
            <Button 
              onClick={handleEditFromView}
              variant="contained"
              startIcon={<EventIcon />}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: 'white',
                fontWeight: 500,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                }
              }}
            >
              Edit Batch
            </Button>
          )}
        </DialogActions>
      </Dialog>
        </CalendarContainer>
      </Container>
      </Fade>
    </LocalizationProvider>
  );
};

export default BatchingCalendar;